import { Component } from '@theme/component';

/**
 * A custom element that displays a wishlist icon.
 * Shows empty icon when no items, active icon when items exist.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} emptyIcon - The empty wishlist icon element.
 * @property {HTMLElement} activeIcon - The active wishlist icon element.
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('wishlist:update', this.onWishlistUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureIconIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('wishlist:update', this.onWishlistUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureIconIsCorrect();
    }
  };

  /**
   * Handles the wishlist update event.
   * @param {CustomEvent} event - The wishlist update event.
   */
  onWishlistUpdate = (event) => {
    const itemCount = event.detail?.itemCount ?? 0;
    this.renderIcon(itemCount);
  };

  /**
   * Renders the appropriate wishlist icon based on item count.
   * @param {number} itemCount - The number of items in the wishlist.
   */
  renderIcon = (itemCount) => {
    const emptyIcon = this.querySelector('.wishlist-icon__empty');
    const activeIcon = this.querySelector('.wishlist-icon__active');

    if (emptyIcon && activeIcon) {
      emptyIcon.classList.toggle('wishlist-icon--hidden', itemCount > 0);
      activeIcon.classList.toggle('wishlist-icon--hidden', itemCount === 0);
    }

    this.classList.toggle('header-actions__wishlist-icon--has-items', itemCount > 0);

    sessionStorage.setItem(
      'wishlist-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Checks if the wishlist icon state is correct.
   */
  ensureIconIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('wishlist-count');

    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderIcon(count);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
